/**
 * Canonical API-error parser for form submissions.
 *
 * Pliny's odl_api exception handler answers a failed write with a JSON:API
 * envelope ({errors: [{status, title, detail}]}, detail = "<field>: <message>"
 * for validation failures), but older endpoints, the RTK Query base query, the
 * raw axios client, and legacy DRF views each hand the frontend a different
 * shape. This module funnels all of them into one result a form can consume:
 * field-attributed errors (rendered inline on their inputs) and form-wide
 * ("non-field") messages (rendered in a banner / toast).
 *
 * Contains:
 * - parseApiError — normalize any supported error into { fieldErrors, nonFieldMessages }
 * - applyApiErrorToFormik — push field errors into a Formik form, return the leftover non-field messages
 * - GENERIC_ERROR_MESSAGE — English fallback used when nothing usable can be extracted
 */

export const GENERIC_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

// DRF names its cross-field / form-wide validation bucket this; it is never a
// real form input, so entries under it always route to the non-field channel.
const NON_FIELD_KEY = 'non_field_errors';

// A leading "<field>: <message>" prefix. Field names are word chars plus dots
// (nested serializer paths like "default_schedule.on_time") — no spaces, which
// keeps ordinary prose ("Not found.") from being mistaken for a field prefix.
const FIELD_PREFIX = /^([A-Za-z0-9_.]+):\s*(.*)$/;

/**
 * Unwrap the response body out of whatever the caller passed: an axios error
 * ({response.data}), an RTK Query error or unwrapped thunk result ({data} /
 * {body}), or the body itself (dict, array, or string).
 *
 * @param {*} error
 * @returns {*} the response body, or null
 */
function resolveBody(error) {
  if (error == null) {
    return null;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error.response?.data !== undefined) {
    return error.response.data;
  }
  if (error.data !== undefined) {
    return error.data;
  }
  if (error.body !== undefined) {
    return error.body;
  }
  if (typeof error.message === 'string' && typeof error.stack === 'string') {
    // A JS Error (raw network failure, serialized thunk rejection) carries no
    // response body — never ingest its own properties as field errors.
    return null;
  }
  return error;
}

function addFieldError(fieldErrors, field, message) {
  if (!fieldErrors[field]) {
    fieldErrors[field] = [];
  }
  fieldErrors[field].push(message);
}

/**
 * Route one envelope entry (or bare string) into the result, honoring the
 * "<field>:" prefix split and the knownFields downgrade.
 */
function ingestDetail(entry, result, knownFields) {
  const detail = typeof entry === 'string' ? entry : (entry?.detail ?? entry?.title);
  if (typeof detail !== 'string' || detail.length === 0) {
    return;
  }
  const match = detail.match(FIELD_PREFIX);
  if (!match) {
    result.nonFieldMessages.push(detail);
    return;
  }
  const [, field, message] = match;
  if (field === NON_FIELD_KEY) {
    result.nonFieldMessages.push(message);
    return;
  }
  if (knownFields && !knownFields.includes(field)) {
    // Can't attribute it to a form input — surface it verbatim (it already
    // reads "field: message") rather than dropping it silently.
    result.nonFieldMessages.push(detail);
    return;
  }
  addFieldError(result.fieldErrors, field, message);
}

/**
 * Route a flat DRF dict ({field: [...], non_field_errors: [...]}) into the
 * result. Values may be arrays or bare scalars.
 */
function ingestFlatDict(body, result, knownFields) {
  Object.entries(body).forEach(([key, value]) => {
    if (value == null) {
      return;
    }
    const messages = Array.isArray(value) ? value : [value];
    messages.forEach(raw => {
      const message = typeof raw === 'string' ? raw : String(raw);
      if (key === NON_FIELD_KEY) {
        result.nonFieldMessages.push(message);
      } else if (knownFields && !knownFields.includes(key)) {
        result.nonFieldMessages.push(`${key}: ${message}`);
      } else {
        addFieldError(result.fieldErrors, key, message);
      }
    });
  });
}

function isEmptyResult(result) {
  return result.nonFieldMessages.length === 0 && Object.keys(result.fieldErrors).length === 0;
}

/**
 * Normalize an API error into field-attributed and form-wide messages.
 *
 * Handles the five shapes seen across the app: the JSON:API envelope
 * ({errors: [{detail}]}, canonical), a bare [{detail}] array, a flat DRF dict
 * ({field: [...], non_field_errors: [...]}), a lone {detail: '...'} or the
 * bare-RPC {details: '...'} variant, and a raw string / unrecognized value
 * (→ generic fallback).
 *
 * @param {*} error - axios error, RTK Query error, thunk rejection, or raw body
 * @param {object} [options]
 * @param {string[]} [options.knownFields] - form's own field names; entries for
 *   any other field downgrade to non-field messages
 * @param {?string} [options.genericMessage] - fallback when nothing usable is
 *   extracted; pass null/'' to suppress the fallback entirely
 * @returns {{ fieldErrors: Object<string, string[]>, nonFieldMessages: string[] }}
 */
export function parseApiError(error, options = {}) {
  const { knownFields, genericMessage = GENERIC_ERROR_MESSAGE } = options;
  const result = { fieldErrors: {}, nonFieldMessages: [] };
  const body = resolveBody(error);

  const errorsArray = Array.isArray(body) ? body : Array.isArray(body?.errors) ? body.errors : null;
  if (errorsArray) {
    errorsArray.forEach(entry => ingestDetail(entry, result, knownFields));
  } else if (typeof body === 'string') {
    // raw string / unknown → generic (handled by the empty-result fallback below)
  } else if (body && typeof body === 'object') {
    // `detail` is DRF's singular key; `details` is the bare-RPC 400 shape some
    // odl_app action endpoints return ({"details": "<message>"}). Both are a
    // lone form-wide string, never a field map.
    if (typeof body.detail === 'string') {
      result.nonFieldMessages.push(body.detail);
    } else if (typeof body.details === 'string') {
      result.nonFieldMessages.push(body.details);
    } else {
      ingestFlatDict(body, result, knownFields);
    }
  }

  if (isEmptyResult(result) && genericMessage) {
    result.nonFieldMessages.push(genericMessage);
  }
  return result;
}

/**
 * Parse an API error and apply its field-attributed messages to a Formik form,
 * returning the form-wide messages the caller still needs to surface (banner /
 * toast).
 *
 * @param {*} error
 * @param {object} options
 * @param {function} [options.setErrors] - Formik setErrors (preferred; replaces the error map)
 * @param {function} [options.setFieldError] - Formik setFieldError (used when setErrors is absent)
 * @param {string[]} [options.knownFields]
 * @param {?string} [options.genericMessage]
 * @returns {string[]} the non-field messages that were not applied to the form
 */
export function applyApiErrorToFormik(error, options = {}) {
  const { setErrors, setFieldError, knownFields, genericMessage } = options;
  const { fieldErrors, nonFieldMessages } = parseApiError(error, { knownFields, genericMessage });

  const flattened = {};
  Object.entries(fieldErrors).forEach(([field, messages]) => {
    flattened[field] = messages.join(' ');
  });

  if (setErrors) {
    setErrors(flattened);
  } else if (setFieldError) {
    Object.entries(flattened).forEach(([field, message]) => setFieldError(field, message));
  }

  return nonFieldMessages;
}
